import React from 'react'
import {
  Modal,
  Button,
  Typography,
  Icon,
  Empty,
  Alert,
  Spin,
  Tag,
  Divider
} from 'antd'
import useVideoSuggestions from './useVideoSuggestions'

const { Title, Text, Paragraph } = Typography

const FIX_TYPE_META = {
  regenerate_same_image: {
    color: 'gold',
    label: 'Regenerate clip'
  },
  image_prompt_suggestion: {
    color: 'blue',
    label: 'Image prompt update'
  },
  negative_prompt_suggestion: {
    color: 'red',
    label: 'Negative prompt update'
  }
}

const formatSeconds = value => {
  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) {
    return null
  }

  const minutes = Math.floor(numericValue / 60)
  const seconds = (numericValue % 60).toFixed(1)
  const paddedSeconds = numericValue >= 60 ? seconds.padStart(4, '0') : seconds

  return minutes > 0 ? `${minutes}:${paddedSeconds}` : `${seconds}s`
}

const formatTimeWindow = suggestion => {
  const start = formatSeconds(suggestion?.error_time)
  const end = formatSeconds(suggestion?.error_end_time)

  if (start && end) {
    return `${start} - ${end}`
  }

  return start || end || 'Time not provided'
}

const renderSuggestionCard = (suggestion, index) => {
  const fixMeta = FIX_TYPE_META[suggestion?.fix_type] || {
    color: 'default',
    label: suggestion?.fix_type || 'Suggestion'
  }

  return (
    <div
      key={`${suggestion?.fix_type || 'suggestion'}-${index}`}
      style={{
        background: '#fff',
        border: '1px solid #e7edf5',
        borderRadius: 16,
        padding: 18,
        boxShadow: '0 8px 28px rgba(15, 23, 42, 0.05)'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 10
        }}
      >
        <Tag color={fixMeta.color} style={{ marginRight: 0 }}>
          {fixMeta.label}
        </Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <Icon type="clock-circle" style={{ marginRight: 6 }} />
          {formatTimeWindow(suggestion)}
        </Text>
      </div>

      <Text
        strong
        style={{
          display: 'block',
          color: '#172033',
          fontSize: 14,
          marginBottom: 8
        }}
      >
        {suggestion?.error_text || 'Potential issue detected'}
      </Text>

      <Paragraph
        style={{
          margin: 0,
          color: '#546072',
          fontSize: 13,
          lineHeight: 1.6
        }}
      >
        {suggestion?.fix_suggestion_text || 'No suggested fix was returned.'}
      </Paragraph>
    </div>
  )
}

const VideoSuggestionsModal = ({
  visible,
  onCancel,
  videoUrl,
  shotList,
  videoId,
  storedReview,
  setFormData
}) => {
  const {
    suggestions,
    isLoading,
    error,
    fetchSuggestions,
    suggestionRequest,
    hasStartedReview,
    startReview,
    progress,
    message
  } = useVideoSuggestions({
    visible,
    videoUrl,
    shotList,
    videoId,
    storedReview,
    setFormData
  })

  const suggestionsByShot = suggestions?.suggestions_by_shot || {}
  const suggestionEntries = Object.entries(suggestionsByShot)
  const totalSuggestions = suggestions?.total_suggestions || 0
  const hasRequestPayload = Boolean(suggestionRequest?.payload)
  const shouldShowIntro = hasRequestPayload && !hasStartedReview && !isLoading

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      width={920}
      centered
      destroyOnClose
      className="video-suggestions-modal"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 14,
              background: 'linear-gradient(135deg, #eef6ff 0%, #fff1dc 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1d4ed8',
              boxShadow: '0 10px 28px rgba(29, 78, 216, 0.16)'
            }}
          >
            <Icon type="bulb" style={{ fontSize: 18 }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              AI Review
            </Title>
          </div>
        </div>
      }
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
        shouldShowIntro ? (
          <Button
            key="start"
            type="primary"
            onClick={startReview}
            disabled={!hasRequestPayload}
          >
            Generate Review
          </Button>
        ) : (
          <Button
            key="refresh"
            type="primary"
            ghost
            onClick={fetchSuggestions}
            disabled={!hasRequestPayload}
            loading={isLoading}
          >
            Refresh Review
          </Button>
        )
      ]}
      bodyStyle={{
        background:
          'linear-gradient(180deg, #f8fbff 0%, #f6f7fb 55%, #ffffff 100%)',
        padding: 24,
        maxHeight: '75vh',
        overflowY: 'auto'
      }}
    >
      {!hasRequestPayload && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              Review needs the final video, shot timing data, and the source
              generation video ID.
            </span>
          }
        />
      )}

      {shouldShowIntro && (
        <div
          style={{
            padding: '32px 0 36px',
            textAlign: 'center',
            maxWidth: 520,
            margin: '0 auto'
          }}
        >
          <Title
            level={4}
            style={{ marginTop: 0, marginBottom: 10, color: '#172033' }}
          >
            Generate AI review now?
          </Title>
          <Paragraph
            style={{
              margin: 0,
              color: '#5b6475',
              fontSize: 14,
              lineHeight: 1.7
            }}
          >
            We will review the current final video and return AI suggestions for
            any issues found. Would you like to continue?
          </Paragraph>
        </div>
      )}

      {hasRequestPayload && isLoading && (
        <div
          style={{
            minHeight: 240,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            textAlign: 'center',
            gap: 10
          }}
        >
          <Spin size="large" tip="Generating review..." />
          {!!message && (
            <Text type="secondary" style={{ maxWidth: 320 }}>
              {message}
            </Text>
          )}
          {typeof progress === 'number' && progress > 0 && (
            <Text type="secondary">{progress}% complete</Text>
          )}
        </div>
      )}

      {hasRequestPayload && !isLoading && error && (
        <Alert
          type="error"
          showIcon
          message="Review failed"
          description={error}
          style={{ borderRadius: 12 }}
        />
      )}

      {hasRequestPayload &&
        hasStartedReview &&
        !isLoading &&
        !error &&
        suggestionEntries.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>No issues were flagged in this review pass.</span>
            }
          />
        )}

      {hasRequestPayload &&
        hasStartedReview &&
        !isLoading &&
        !error &&
        suggestionEntries.length > 0 && (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
                marginBottom: 20
              }}
            >
              <div>
                <Text strong style={{ fontSize: 15, color: '#172033' }}>
                  {totalSuggestions} issue{totalSuggestions === 1 ? '' : 's'}{' '}
                  found
                </Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Across {suggestionEntries.length} shot
                    {suggestionEntries.length === 1 ? '' : 's'}
                  </Text>
                </div>
              </div>
              <Tag
                color="geekblue"
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  marginRight: 0
                }}
              >
                {suggestionRequest.sceneCount} scene
                {suggestionRequest.sceneCount === 1 ? '' : 's'} reviewed
              </Tag>
            </div>

            {suggestionEntries.map(([shotLabel, shotSuggestions], index) => (
              <div key={shotLabel} style={{ marginBottom: 24 }}>
                {index > 0 && (
                  <Divider
                    style={{ margin: '0 0 24px', background: '#e8edf5' }}
                  />
                )}

                <div style={{ marginBottom: 14 }}>
                  <Text
                    strong
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 15,
                      color: '#172033'
                    }}
                  >
                    <Icon type="video-camera" style={{ color: '#1677ff' }} />
                    {shotLabel}
                  </Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {shotSuggestions.length} suggestion
                      {shotSuggestions.length === 1 ? '' : 's'}
                    </Text>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: 16
                  }}
                >
                  {shotSuggestions.map(renderSuggestionCard)}
                </div>
              </div>
            ))}
          </div>
        )}
    </Modal>
  )
}

export default VideoSuggestionsModal
